import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import axios from 'src/utils/axios';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import TooltipMUI6 from 'src/components/TooltipMUI6';

// The way back to a dismissed research note.
//
// Dismissing is reversible by design: it hides the bar above the compose box
// and nothing else, so the note has to stay one click away or the dismiss
// becomes a destructive act on incomplete information - which is the same
// failure as feedback that could not be corrected.
//
// Lives in the toolbar group beside the feedback and ops-notes icons rather
// than where the bar used to be, so a dismissed note costs no vertical space
// at all. Present only when there IS a dismissed note for this contact's
// current draft; the rest of the time it renders nothing.
function AiResearchNoteRestore({
  contactId = null,
  refreshKey = '',
  onStateChanged = () => {},
}) {
  const [research, setResearch] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) { setResearch(null); return; }
    try {
      const { data } = await axios.get('/api/ai-support-drafts/research', { params: { contactId } });
      setResearch(data?.enabled ? data.research : null);
    } catch (error) {
      setResearch(null);
    }
  }, [contactId]);

  // refreshKey is bumped when the panel above dismisses a note, so this icon
  // appears straight away rather than only after a reload.
  useEffect(() => {
    setIsOpen(false);
    load();
  }, [load, refreshKey]);

  const restore = async () => {
    if (!research?.id || !contactId) return;
    try {
      await axios.put('/api/ai-support-drafts/research/state', {
        contactId,
        aiSupportDraftId: research.id,
        dismissed: false,
      });
      // Clearing it locally brings the full bar straight back on the next
      // render rather than waiting for a refetch.
      setResearch((prev) => (prev ? { ...prev, dismissedAt: null } : prev));
      setIsOpen(false);
      onStateChanged();
    } catch (error) {
      // Leave the dialog open; the note is still readable either way.
    }
  };

  if (!research?.dismissedAt || !research?.note) return null;

  return (
    <>
      <TooltipMUI6 title="Dismissed research note">
        <IconButton
          aria-label="Show dismissed research note"
          size="small"
          color="secondary"
          onClick={() => setIsOpen(true)}
        >
          <ScienceOutlinedIcon fontSize="small" />
        </IconButton>
      </TooltipMUI6>

      <ModalDialogMUI6
        isVisible={isOpen}
        title="Research note (internal)"
        okText="Bring it back"
        closeText="Close"
        onOk={restore}
        onClose={() => setIsOpen(false)}
        content={(
          <Box sx={{ minWidth: '320px', maxWidth: '640px' }}>
            {research.status === 'low-confidence' && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', marginBottom: '8px' }}>
                The investigation took an unusually long path, so treat these facts as unverified.
              </Typography>
            )}
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {research.note}
            </Typography>
          </Box>
        )}
      />
    </>
  );
}

AiResearchNoteRestore.propTypes = {
  contactId: PropTypes.number,
  // Bumped when the panel above changes the note's state.
  // A composite string, not a counter: the two signals behind it move
  // independently and summing them can repeat a value.
  refreshKey: PropTypes.string,
  // Called when this control brings a note back, so the panel reappears.
  onStateChanged: PropTypes.func,
};

export default AiResearchNoteRestore;
